import React from "react";

import ProfilePictureRounded from "../ProfilePictureRounded";

// Up to this many faces, then a +N chip. Four is what fits beside the group actions without the stack starting to read as a crowd.
const MAX_FACES = 4;

const initialsOf = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "?";

interface OrganizationPreviewProps {
  organization: { name?: string; members?: any[] };
  /** Drops the name and member count, leaving the bare stack — for tight rows. */
  compact?: boolean;
  className?: string;
}

// The organization's members as an overlapping avatar stack inside a pill, with the org name and member count beside it. The faces alone never said whose organization they were, and a bare stack capped at three silently hid everyone past the third.
const OrganizationPreview = ({ organization, compact = false, className = "" }: OrganizationPreviewProps) => {
  const members = organization?.members ?? [];
  if (members.length === 0) return null;

  const shown = members.slice(0, MAX_FACES);
  const overflow = members.length - shown.length;

  const stack = (
    <span className="flex items-center">
      {shown.map((member, index) => (
        <span
          key={member?.id ?? index}
          title={member?.name || "Unknown member"}
          className={index === 0 ? "" : "-ml-2.5"}
        >
          <ProfilePictureRounded
            pictureURL={member?.profilePictureUrl}
            diameter={30}
            alt={member?.name || "Organization member"}
            className="border-white shadow-sm"
            fallback={
              <span className="flex h-full w-full items-center justify-center bg-eai-hero-accent font-outfit text-eai-caption font-medium text-white">
                {initialsOf(member?.name)}
              </span>
            }
          />
        </span>
      ))}
      {overflow > 0 && (
        <span
          title={`${overflow} more member${overflow === 1 ? "" : "s"}`}
          className="-ml-2.5 flex h-[30px] w-[30px] items-center justify-center rounded-full border-2 border-white bg-eai-surface-hover font-outfit text-eai-caption font-medium text-eai-ink-muted shadow-eai-subtle"
        >
          +{overflow}
        </span>
      )}
    </span>
  );

  if (compact) return <div className={`flex items-center ${className}`}>{stack}</div>;

  return (
    <div
      className={`inline-flex items-center gap-2.5 rounded-full border border-eai-border bg-eai-surface-card py-1 pl-1.5 pr-3.5 shadow-eai-subtle ${className}`}
    >
      {stack}
      <span className="min-w-0 leading-tight">
        <span className="block truncate font-outfit text-eai-body-xs text-eai-ink-soft">
          {organization?.name || "Organization"}
        </span>
        <span className="block font-inter text-eai-caption text-eai-ink-faint">
          {members.length} member{members.length === 1 ? "" : "s"}
        </span>
      </span>
    </div>
  );
};

export default OrganizationPreview;
